import * as tiptapHtml from '@tiptap/html';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import DOMPurify from 'isomorphic-dompurify';
import StarterKit from '@tiptap/starter-kit';

/**
 * Canonical TipTap extension set for CMS legal pages.
 *
 * ⚠️ CONTRACT: this array is the single source of truth for which TipTap
 * nodes/marks a legal page may contain. `generateHTML` SILENTLY DROPS any node
 * or mark that is not represented here, so clowncar authoring (JCE-7271) and
 * jce-web rendering (JCE-7274) MUST author/render with a matching set. Adding a
 * node here without adding it to the authoring editor (or vice-versa) will
 * delete content from live legal pages with no error.
 *
 * Note: `@tiptap/starter-kit` already bundles and enables `@tiptap/extension-link`
 * (with StarterKit heading levels restricted to 1-3 below), so `Link` must NOT
 * be added again — a duplicate extension name throws inside `generateHTML`.
 */
export const LEGAL_TIPTAP_EXTENSIONS = [
  StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
  Table,
  TableRow,
  TableHeader,
  TableCell,
];

type GenerateHtml = typeof tiptapHtml.generateHTML;
type TiptapDoc = Parameters<GenerateHtml>[0];

/**
 * Convert a TipTap JSON document (as stored in `LegalPageEntity.content`) into
 * sanitized HTML suitable for `dangerouslySetInnerHTML`.
 *
 * Safety (defense in depth, primary -> belt-and-suspenders):
 *  1. The restricted extension schema above is the primary sanitizer — nodes,
 *     marks, and attributes outside it cannot be emitted (no `<script>`,
 *     `<iframe>`, `on*=` handlers, or arbitrary `style`).
 *  2. `@tiptap/extension-link` (via StarterKit) neutralizes dangerous hrefs
 *     (`javascript:`, `data:`, ...) to `href=""` and always sets
 *     `rel="noopener noreferrer nofollow"`.
 *  3. A final `DOMPurify.sanitize()` pass with the default config.
 *  4. Fail-closed JSON parsing — malformed content renders nothing.
 *
 * SSR build selection: `@tiptap/html`'s default build asserts a browser
 * environment (throws when `window` is undefined), while `@tiptap/html/server`
 * (happy-dom) asserts the opposite. We pick the build at runtime using the exact
 * `typeof window` condition each one checks. The server build is loaded lazily
 * (only in the no-`window` branch) so that:
 *  - the client webpack bundle never pulls in happy-dom (it is additionally
 *    aliased to `false` in `webpack/shared.config.js` as a safety net);
 *  - jsdom Jest (window defined) never loads the ESM-only happy-dom package;
 *  - the Node SSR build (webpack `target: node`) externalizes and `require`s the
 *    happy-dom server build at runtime.
 */
export function renderLegalContentHtml(contentJson: string): string {
  let doc: TiptapDoc;
  try {
    doc = JSON.parse(contentJson) as TiptapDoc;
  } catch {
    return '';
  }

  const generateHTML: GenerateHtml =
    typeof window === 'undefined'
      ? (require('@tiptap/html/server') as typeof import('@tiptap/html')).generateHTML
      : tiptapHtml.generateHTML;

  const html = generateHTML(doc, LEGAL_TIPTAP_EXTENSIONS);

  return DOMPurify.sanitize(html);
}
